// Shared helpers for the Sales Lead screens (api/inhouse_app/salesLead/*).
// Business rules live on the server (SalesLeadService); these helpers only call it and format.
import axios from 'axios';
import { Geolocation } from '@capacitor/geolocation';

const META_KEY = 'sales_lead_meta_v3'; // v3: product catalogue (products, contract months)

export const STAGE_COLORS: Record<string, string> = {
  New: '#64748b',
  Contacted: '#0891b2',
  Qualified: '#2563eb',
  Demo: '#4f46e5',
  'Proposal Sent': '#d97706',
  Negotiation: '#ea580c',
  Converted: '#0d9488',
  Won: '#059669',
  Lost: '#dc2626',
  Disqualified: '#6b7280',
};

// Fallbacks for servers that do not send option_details yet. The lists themselves are editable
// in the admin panel (Sales -> Lead Dropdown Options) and arrive in meta.
export const PRIORITY_COLORS: Record<string, string> = { Hot: '#dc2626', Warm: '#d97706', Cold: '#0891b2' };
const SENTIMENT_COLORS: Record<string, string> = { Positive: '#16a34a', Neutral: '#6b7280', Negative: '#dc2626' };
const FALLBACK_DEFAULTS: Record<string, string> = { priority: 'Warm', meeting_type: 'Intro', meeting_mode: 'In-person', activity_type: 'Call' };

function optionDetail(meta: any, list: string, label: string): any {
  const details = meta && meta.option_details ? meta.option_details[list] : null;
  return details ? details.find((o: any) => o.label === label) || null : null;
}

/** Badge colour of a priority / sentiment option. */
export function optionColor(meta: any, list: 'priority' | 'sentiment', label: string): string {
  const option = optionDetail(meta, list, label);
  if (option && option.color) {
    return option.color;
  }
  return (list === 'priority' ? PRIORITY_COLORS : SENTIMENT_COLORS)[label] || '#94a3b8';
}

/** The option pre-selected in forms ('' when the list has none). */
export function optionDefault(meta: any, list: string): string {
  if (meta && meta.option_defaults && list in meta.option_defaults) {
    return meta.option_defaults[list] || '';
  }
  return FALLBACK_DEFAULTS[list] || '';
}

/** Does logging this activity type record a GPS fix? */
export function capturesLocation(meta: any, activityType: string): boolean {
  const option = optionDetail(meta, 'activity_type', activityType);
  return option ? !!option.capture_location : activityType === 'Visit';
}

/** Does this lost / transfer reason need a written explanation? */
export function needsNote(meta: any, list: 'lost_reason' | 'transfer_reason', reason: string): boolean {
  const option = optionDetail(meta, list, reason);
  return option ? !!option.needs_note : reason === 'Other';
}

export function stageLabel(stage: string): string {
  return stage === 'Converted' ? 'Converted – Pending Approval' : stage;
}

function credentials() {
  return { username: window.localStorage.username, password: window.localStorage.password };
}

/** POST to a salesLead endpoint with the stored credentials. Server errors are shown by the global axios interceptor. */
export function leadApi(endpoint: string, data: Record<string, any> = {}, quiet = false) {
  return axios.post('salesLead/' + endpoint, { ...credentials(), ...data }, quiet ? ({ skipLoading: true } as any) : undefined);
}

/** Multipart variant for requests with an attachment. */
export function leadApiForm(endpoint: string, data: Record<string, any>, file: File | null) {
  const form = new FormData();
  const all: Record<string, any> = { ...credentials(), ...data };
  for (const key of Object.keys(all)) {
    if (all[key] !== null && all[key] !== undefined && all[key] !== '') {
      form.append(key, String(all[key]));
    }
  }
  if (file) {
    form.append('attachment', file);
  }
  return axios.post('salesLead/' + endpoint, form);
}

/** Dropdown options and user list. Cached for the session; refreshed in the background. */
export async function loadMeta(force = false): Promise<any> {
  const cached = window.sessionStorage.getItem(META_KEY);
  if (cached && !force) {
    leadApi('meta', {}, true).then(r => window.sessionStorage.setItem(META_KEY, JSON.stringify(r.data))).catch(() => undefined);
    return JSON.parse(cached);
  }
  const response = await leadApi('meta', {}, true);
  window.sessionStorage.setItem(META_KEY, JSON.stringify(response.data));
  return response.data;
}

/** Best-effort GPS fix for visit/meeting proof. Returns nulls if permission is denied or it times out. */
export async function currentPosition(): Promise<{ lat: number | null; lng: number | null }> {
  try {
    const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 });
    return { lat: pos.coords.latitude, lng: pos.coords.longitude };
  } catch (e) {
    return { lat: null, lng: null };
  }
}

/** "2026-09-23 14:30:00" -> "23 Sep, 2:30 PM" */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }
  const date = new Date(String(value).replace(' ', 'T'));
  if (isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) + ', ' +
    date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export function formatMoney(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return 'Tk ' + Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/** Value for <input type="datetime-local"> from a server datetime. */
export function toLocalInput(value: string | null | undefined): string {
  return value ? String(value).replace(' ', 'T').substring(0, 16) : '';
}

export function isPast(value: string | null | undefined): boolean {
  return !!value && new Date(String(value).replace(' ', 'T')).getTime() < Date.now();
}
